import { getCustomerCompanyListQueryKey } from "@boundless/bl-api-client/customer/services"
import { usePartnersCompanyCreate } from "@boundless/bl-api-client/partners/services"
import { PartnersCompanyCreateBody } from "@boundless/bl-api-client/partners/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"

import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { FormSection } from "@/components/form-elements/FormSection"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

interface Props {
  onBack: () => void
  onSuccess: () => void
}

export function AddCompanyForm({ onBack, onSuccess }: Props) {
  const queryClient = useQueryClient()

  const { mutate: createCompany, isPending } = usePartnersCompanyCreate({
    mutation: {
      onSuccess: () => {
        const companyListKey = getCustomerCompanyListQueryKey()
        queryClient.invalidateQueries({ queryKey: companyListKey })

        onSuccess()
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  const form = useForm({
    resolver: zodResolver(PartnersCompanyCreateBody),
    defaultValues: {
      customerEmail: "",
      customerFirstName: "",
      customerLastName: "",
      customerPhone: "",
      legalName: "",
    },
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => createCompany({ data }))}
        className="@container/form flex flex-col gap-6"
      >
        <FormSection>
          <FormInput
            name="legalName"
            placeholder="Legal Business Name"
            className="col-span-2"
          />
          <FormInput
            name="customerEmail"
            placeholder="Client Email"
            className="col-span-2"
          />
          <FormInput name="customerFirstName" placeholder="Client First Name" />
          <FormInput name="customerLastName" placeholder="Client Last Name" />
          <FormInputPhone name="customerPhone" placeholder="Client Phone" />
        </FormSection>

        <FormConsentText action="clicking 'Create' below" />

        <div className="flex flex-row justify-end space-x-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          {isPending ? (
            <LoadingButton />
          ) : (
            <Button type="submit">Create</Button>
          )}
        </div>
      </form>
    </Form>
  )
}
